import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { getFileUrl } from '../api/files';
import type { Submission } from '../api/types';

interface SubmittedAnswerProps {
  /** Optional: a submission with no content must not take the view down. */
  content: Submission['content'] | undefined;
}

/**
 * A team's own answer, played back to them.
 *
 * The coordination could always see what was submitted; the people who
 * submitted it could not — reaching their answer meant pressing "change answer"
 * and entering the edit flow just to read it back (#482).
 *
 * Every media kind a challenge can produce is covered, so this stays correct as
 * challenge types are added: whatever key the submission carries is rendered.
 */
const SubmittedAnswer: React.FC<SubmittedAnswerProps> = ({ content }) => {
  const { t } = useTranslation('runs');

  if (!content) return null;

  const text = typeof content.text === 'string' ? content.text : null;
  const photoUrl = typeof content.photo_url === 'string' ? content.photo_url : null;
  const audioUrl = typeof content.audio_url === 'string' ? content.audio_url : null;
  const videoUrl = typeof content.video_url === 'string' ? content.video_url : null;
  // QUIZ stores `selected_option`; QR answers carry the scanned payload.
  const option = typeof content.selected_option === 'string' ? content.selected_option : null;
  const scanned = typeof content.scanned_code === 'string' ? content.scanned_code : null;

  if (!text && !photoUrl && !audioUrl && !videoUrl && !option && !scanned) return null;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
        {t('participant.challenge.yourAnswer')}
      </Typography>

      {text && (
        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap' }}>
          <Typography variant="body2">{text}</Typography>
        </Box>
      )}

      {option && <Typography variant="body2">{option}</Typography>}

      {scanned && (
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {scanned}
        </Typography>
      )}

      {photoUrl && (
        <Box
          component="img"
          src={getFileUrl(photoUrl)}
          alt={t('participant.challenge.yourAnswer')}
          sx={{ maxWidth: '100%', maxHeight: 260, borderRadius: 1, display: 'block' }}
        />
      )}

      {audioUrl && (
        <Box component="audio" controls src={getFileUrl(audioUrl)} sx={{ width: '100%', mt: 0.5 }} />
      )}

      {videoUrl && (
        <Box
          component="video"
          controls
          playsInline
          src={getFileUrl(videoUrl)}
          sx={{ maxWidth: '100%', maxHeight: 260, borderRadius: 1, display: 'block', mt: 0.5 }}
        />
      )}
    </Box>
  );
};

export default SubmittedAnswer;
